import React, { memo, type JSX } from 'react';
import ActiveTalentTreeNode from '../ActiveTalentTreeNode/ActiveTalentTreeNode';
import ChoiceTalentTreeNode, {
  type ChoiceTalentTreeNodeProps,
} from '../ChoiceTalentTreeNode/ChoiceTalentTreeNode';
import PassiveTalentTreeNode, {
  type PassiveTalentTreeNodeProps,
} from '../PassiveTalentTreeNode/PassiveTalentTreeNode';
import {
  type RetailWarcraftTalentTreeBuildNode,
  type RetailWarcraftTalentTreeEntry,
  RetailWarcraftTalentTreeEntryType,
  RetailWarcraftTalentTreeNodeType,
} from '../../typings/viewModels/retailWarcraftTalentTreeBuildNode';

export type TalentTreeNodeProps = {
  node: RetailWarcraftTalentTreeBuildNode;
  spellModifierId: number;
} & Pick<
  Partial<ChoiceTalentTreeNodeProps>,
  'showChoices' | 'tooltipClassName'
> &
  Pick<PassiveTalentTreeNodeProps, 'renderMultiplePointsIndicator'>;

function selectedAbility(
  buildNode: RetailWarcraftTalentTreeBuildNode,
): RetailWarcraftTalentTreeEntry {
  return buildNode.entry ?? buildNode.node.abilities[0];
}

export function heatMapPercentageClassName(percent: number): string {
  percent = percent * 100;

  if (percent >= 95) {
    return 'artifact';
  }

  if (percent >= 90) {
    return 'legendary';
  }

  if (percent >= 60) {
    return 'epic';
  }

  if (percent >= 35) {
    return 'rare';
  }

  if (percent >= 20) {
    return 'uncommon';
  }

  return 'common';
}

export function heatMapPercentageForEntry(
  node: RetailWarcraftTalentTreeBuildNode,
  entry: RetailWarcraftTalentTreeEntry,
): number {
  if (!node.heatMapPercent) {
    return 0;
  }

  for (let i = 0; i <= node.node.abilities.length; i++) {
    if (node.node.abilities[i].id === entry.id) {
      const pct = node.heatMapPercent.values[i];
      if (
        node.heatMapPercent.values.length > 1 &&
        node.node.type !== RetailWarcraftTalentTreeNodeType.Capstone
      ) {
        const totalPickRate = node.heatMapPercent.values.reduce(
          (a, b) => a + b,
          0,
        );

        if (totalPickRate === 0) {
          return 0;
        }

        return pct / totalPickRate;
      } else {
        return pct;
      }
    }
  }

  return 0;
}

function TalentTreeNode({
  node,
  showChoices,
  tooltipClassName,
  renderMultiplePointsIndicator,
  spellModifierId,
}: TalentTreeNodeProps): JSX.Element {
  if (
    node.node.type === RetailWarcraftTalentTreeNodeType.Choice ||
    node.node.type === RetailWarcraftTalentTreeNodeType.Capstone
  ) {
    return (
      <ChoiceTalentTreeNode
        node={node}
        showChoices={showChoices ?? true}
        tooltipClassName={tooltipClassName}
        spellModifierId={spellModifierId}
        renderMultiplePointsIndicator={renderMultiplePointsIndicator}
      />
    );
  }

  const entry = selectedAbility(node);
  if (entry.type === RetailWarcraftTalentTreeEntryType.Active) {
    return (
      <ActiveTalentTreeNode
        node={node}
        entry={entry}
        spellModifierId={spellModifierId}
      />
    );
  }

  return (
    <PassiveTalentTreeNode
      node={node}
      entry={entry}
      renderMultiplePointsIndicator={renderMultiplePointsIndicator}
      spellModifierId={spellModifierId}
    />
  );
}

export default memo(TalentTreeNode);
